import { useEffect, useRef, useCallback } from 'react'
import dingAudio from '~/assets/audio/ding.mp3'
import audioNewOrder from '~/assets/audio/notify.aac'

export const useNotificationSound = () => {
  const dingAudioRef = useRef<HTMLAudioElement | null>(null)
  const orderAudioRef = useRef<HTMLAudioElement | null>(null)
  const audioUnlockedRef = useRef(false)

  const initAudioInstances = useCallback(() => {
    if (!dingAudioRef.current) {
      dingAudioRef.current = new Audio(dingAudio)
      dingAudioRef.current.preload = 'auto'
      dingAudioRef.current.load()
    }

    if (!orderAudioRef.current) {
      orderAudioRef.current = new Audio(audioNewOrder)
      orderAudioRef.current.preload = 'auto'
      orderAudioRef.current.load()
    }
  }, [])

  const unlockAudio = useCallback(() => {
    if (audioUnlockedRef.current) return

    initAudioInstances()

    // Mute first, play and pause to unlock audio context on iOS
    if (dingAudioRef.current) {
      dingAudioRef.current.muted = true
    }
    if (orderAudioRef.current) {
      orderAudioRef.current.muted = true
    }

    const unlockPromises = [
      dingAudioRef.current?.play().then(() => dingAudioRef.current?.pause()),
      orderAudioRef.current?.play().then(() => orderAudioRef.current?.pause()),
    ].filter(Boolean)

    Promise.all(unlockPromises)
      .then(() => {
        // Unmute after unlocking
        if (dingAudioRef.current) {
          dingAudioRef.current.muted = false
        }
        if (orderAudioRef.current) {
          orderAudioRef.current.muted = false
        }
        audioUnlockedRef.current = true
      })
      .catch(() => {
        // Silently handle unlock failures
      })
  }, [initAudioInstances])

  const playNewTaskSound = useCallback(() => {
    initAudioInstances()

    if (!dingAudioRef.current) return

    // Reset to start if already playing
    dingAudioRef.current.currentTime = 0

    const playPromise = dingAudioRef.current.play()

    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // @NOTE: read https://developer.chrome.com/blog/autoplay/
        // Retry unlock if not yet unlocked
        if (!audioUnlockedRef.current) {
          unlockAudio()
        }
      })
    }
  }, [initAudioInstances, unlockAudio])

  const playNewOrderSound = useCallback(() => {
    initAudioInstances()

    if (!orderAudioRef.current) return

    // Reset to start if already playing
    orderAudioRef.current.currentTime = 0

    const playPromise = orderAudioRef.current.play()

    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // follow https://developer.chrome.com/blog/autoplay/
        // Retry unlock if not yet unlocked
        if (!audioUnlockedRef.current) {
          unlockAudio()
        }
      })
    }
  }, [initAudioInstances, unlockAudio])

  useEffect(() => {
    // Initialize audio instances
    initAudioInstances()

    // Setup unlock listeners
    const unlockEvents = ['touchstart', 'touchend', 'mousedown', 'click']
    const handleUnlock = () => {
      unlockAudio()
      unlockEvents.forEach((event) => {
        document.removeEventListener(event, handleUnlock)
      })
    }

    unlockEvents.forEach((event) => {
      document.addEventListener(event, handleUnlock, {
        once: true,
        passive: true,
      })
    })

    return () => {
      // Cleanup listeners on unmount
      unlockEvents.forEach((event) => {
        document.removeEventListener(event, handleUnlock)
      })
    }
  }, [initAudioInstances, unlockAudio])

  // return {
  //   playNewTaskSound,
  //   playNewOrderSound,
  // }

  return {
    playNewTaskSound: () => {},
    playNewOrderSound: () => {},
  }
}
